// client/src/components/pages/LexiStar/Routes/RouteCards.jsx
//
// The routes of one lane as cards (LS-09 §5.11): the primary route first, then
// the alternates, then proposed and retired ones. Travel within ANY approved
// route's corridor is compliant (FR-ROUT-005); "primary" is the one planners
// get by default (the oldest approved route of the lane).

import React from 'react';
import { FaPlus, FaExclamationTriangle } from 'react-icons/fa';
import { formatKm, formatMinutes } from './routeGeometry';

const STATUS_STYLES = {
  approved: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  proposed: 'bg-amber-50 text-amber-800 ring-amber-200',
  retired: 'bg-slate-100 text-slate-500 ring-slate-200',
};

const STATUS_LABELS = { approved: 'Approved', proposed: 'Proposed', retired: 'Retired' };

const rank = (r) =>
  r.is_primary ? 0 : r.status === 'approved' ? 1 : r.status === 'proposed' ? 2 : 3;

export function sortRoutes(routes) {
  return [...routes].sort(
    (a, b) =>
      rank(a) - rank(b) ||
      String(a.created_at).localeCompare(String(b.created_at)) ||
      String(a.code).localeCompare(String(b.code))
  );
}

export function StatusPill({ status }) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ring-1 ring-inset ${
        STATUS_STYLES[status] || STATUS_STYLES.retired
      }`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function RouteCards({ routes, selectedId, onSelect, onNew, canManage }) {
  const sorted = sortRoutes(routes);
  return (
    <div className="flex gap-3 overflow-x-auto pb-1" role="list" aria-label="Routes on this lane">
      {sorted.map((r) => {
        const selected = r.id === selectedId;
        return (
          <button
            key={r.id}
            type="button"
            role="listitem"
            onClick={() => onSelect(r)}
            aria-current={selected ? 'true' : undefined}
            className={`flex-shrink-0 w-60 text-left rounded-xl border p-3 transition-all ${
              selected
                ? 'border-primary-500 ring-2 ring-primary-500/30 bg-surface'
                : 'border-slate-200 bg-surface hover:border-slate-300'
            } ${r.status === 'retired' ? 'opacity-70' : ''}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono text-xs font-semibold text-slate-700 truncate">
                {r.code}
              </span>
              <StatusPill status={r.status} />
            </div>
            <p className="mt-1 text-sm font-medium text-slate-900 truncate">{r.name}</p>
            <p className="mt-0.5 text-[11px] uppercase tracking-wide font-semibold text-slate-400">
              {r.is_primary ? 'Primary' : r.status === 'approved' ? 'Alternate' : ' '}
            </p>
            {r.current_version > 0 ? (
              <dl className="mt-2 grid grid-cols-3 gap-1 text-xs">
                <div>
                  <dt className="text-slate-400">Distance</dt>
                  <dd className="font-medium text-slate-800 tabular-nums">
                    {formatKm(r.planned_km)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-400">Time</dt>
                  <dd className="font-medium text-slate-800 tabular-nums">
                    {formatMinutes(r.planned_min)}
                  </dd>
                </div>
                <div>
                  <dt className="text-slate-400">Corridor</dt>
                  <dd className="font-medium text-slate-800 tabular-nums">{r.corridor_m} m</dd>
                </div>
              </dl>
            ) : (
              <p className="mt-2 text-xs text-amber-700">No geometry yet: add a version.</p>
            )}
            <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
              <span>{r.current_version > 0 ? `Version ${r.current_version}` : 'No versions'}</span>
              {r.active_advisories > 0 && (
                <span className="inline-flex items-center gap-1 text-orange-700 font-medium">
                  <FaExclamationTriangle size={10} aria-hidden="true" />
                  {r.active_advisories} advisory{r.active_advisories === 1 ? '' : 'ies'}
                </span>
              )}
            </div>
          </button>
        );
      })}
      {canManage && (
        <button
          type="button"
          onClick={onNew}
          className="flex-shrink-0 w-44 rounded-xl border-2 border-dashed border-slate-300 text-slate-500 hover:border-primary-400 hover:text-primary-600 flex flex-col items-center justify-center gap-1.5 p-3 text-sm font-medium"
        >
          <FaPlus aria-hidden="true" />
          {routes.length ? 'Add an alternate' : 'Add the first route'}
        </button>
      )}
    </div>
  );
}
